import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from 'styled-components';
import { Button, ButtonSize, ButtonColor, Table, TableRow } from '@code/innovance-react-ui-kit';

import {
  WarningText,
  Title,
  Subtitle,
  Separator,
  TableWrapper,
  Form,
} from 'src/front/components/FormStyles';
import CustomerSearchForm, {
  SearchFormData,
  customerSearchDefaultValues,
} from 'src/front/components/CustomerSearchForm';

import {
  useCustomerSearchDispatch,
  useCustomerSearchState,
  useClearDispatch,
} from 'src/front/contexts/ViewCreditsContext';
import { Customer } from 'src/front/api/apiInstance';

import { formatDate } from 'src/front/common/utils/dateTimeUtils';

const Outer = styled.div`
  padding: 0 0 20px;
`;

const customerTableHeaders = [
  'general_customerName_label',
  'general_patronymic_label',
  'general_customerTable_birth_title',
  'general_gender_label',
  'general_customerTable_document_title',
];

interface Props {
  goToNextStep: () => void;
}
export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}
const ClientSearch: FC<Props> = ({ goToNextStep }) => {
  const dispatch = useCustomerSearchDispatch();
  const clearCustomerSearch = useClearDispatch();

  const {
    customerSearchValues,
    customerPageNumber: initialCustomerPage,
  } = useCustomerSearchState();
  const { t } = useTranslation();

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );

  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow | undefined>(
    customerSearchValues?.selectedCustomer
  );

  const [customerPage, setCustomerPage] = useState<number>(initialCustomerPage || 1);
  const [hasCustomerNextPage, setHasCustomerNextPage] = useState<boolean>(false);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);

  const [customerWarning, setCustomerWarning] = useState<string>();

  const loadCustomerList = useCallback(
    async (
      pageNumber = customerPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<void> => {
      try {
        setCustomersLoading(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || customerPage,
          refresh: !pageNumber || forceRefresh,
        });

        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setCustomerList(customers);
        setHasCustomerNextPage(data.hasNextPage);
        setCustomerPage(pageNumber || 1);
        setCustomersLoading(false);
        setCustomerWarning(data.warning);
      } catch (error) {
        // TODO (ozgunbal): show error on modal or handle with error boundary
      }
    },
    [customerPage, searchFormValues]
  );

  const handleSelectCustomer = useCallback(
    async (customer: TableRow | undefined): Promise<void> => {
      clearCustomerSearch();

      setSelectedCustomer(customer);
    },
    [clearCustomerSearch]
  );

  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      customerSearchValues?.selectedCustomer && (await Promise.all([loadCustomerList()]));
    };

    if (!customerList && customerSearchValues?.selectedCustomer?.id && !customersLoading) {
      loadPreviousValues();
    }
  }, [customerList, customerSearchValues, customersLoading, loadCustomerList]);

  const clearPreviousValues = (): void => {
    setCustomerList([]);
    setSelectedCustomer(undefined);
    setCustomerWarning('');
  };

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    clearPreviousValues();
    await loadCustomerList(1, true, formData);
  };

  const handleContinue = (): void => {
    if (selectedCustomer) {
      // TODO: (emre.yildirim) this value keeping issue will be handled with mw
      dispatch({
        customerId: selectedCustomer.id,
        customerNumber: selectedCustomer.customerNumber,
        customerSearchValues: {
          ...searchFormValues,
          selectedCustomer,
        },
        customerPageNumber: customerPage,
      });
      goToNextStep();
    }
  };

  return (
    <Form>
      <Outer>
        <Title>
          <Trans i18nKey="viewCredits_title" />
        </Title>
        <Separator />
        <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
        {customerList ? (
          <>
            <Separator />
            <Subtitle>
              <Trans i18nKey="general_clientList_text" />
            </Subtitle>
            <TableWrapper>
              <Table
                emptyTableText={t('general_components_notFound_errorMessage')}
                hasNextPage={hasCustomerNextPage}
                headers={customerTableHeaders.map((head) => t(head))}
                items={customerList}
                loading={customersLoading}
                loadPageItems={loadCustomerList}
                onSelect={handleSelectCustomer}
                pageNumber={customerPage}
                selectedItem={selectedCustomer}
                widths={['19', '15', '30', '13', '23']}
                clearItemOnPageChange
                withRadio
              />
            </TableWrapper>
            {customerWarning ? <WarningText>{customerWarning}</WarningText> : null}

            {selectedCustomer ? (
              <Button
                color={ButtonColor.Primary}
                onClick={handleContinue}
                size={ButtonSize.Medium}
                text={t('general_continue_button')}
                type="button"
              />
            ) : null}
          </>
        ) : null}
      </Outer>
    </Form>
  );
};

export default ClientSearch;
